import PouchDB from "pouchdb";
import { useCallback, useState } from "react";
import { useDatabases, useInventoryDatabases } from "./useDatabases";
import { DatabaseIdentifier } from "./manager";
import { useCurrentInventoryId } from "../url";
import { InventoryGeometryItem } from "./types/inventories";
import { useHandlePouchChanges } from "./useHandlePouchChanges";

interface PouchCreateReturn<T> {
  execute: (doc: PouchDB.Core.PostDocument<T>) => Promise<T & PouchDB.Core.IdMeta & PouchDB.Core.RevisionIdMeta>;
  creating: boolean;
  error: unknown | null;
}

function usePouchCreate<T>(database: PouchDB.Database<T> | undefined, dependencies: DatabaseIdentifier[]): PouchCreateReturn<T> {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<unknown | null>(null);

  const handlePouchChanges = useHandlePouchChanges();

  const execute = useCallback(
    async (doc: PouchDB.Core.PostDocument<T>) => {
      if (!database) {
        throw new Error("Database not available");
      }
      try {
        setCreating(true);
        setError(null);
        console.log("[POUCH CREATE]", database.name, doc);
        const resp = await database.post(doc);
        handlePouchChanges(dependencies).then(() => {});
        return { ...doc, _id: resp.id, _rev: resp.rev };
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setCreating(false);
      }
    },
    [database, dependencies, handlePouchChanges]
  );

  return { creating, error, execute };
}

export function useCreateProject() {
  const { projects } = useDatabases();
  return usePouchCreate(projects, ["projects"]);
}

export function useCreateSetting() {
  const { settings } = useDatabases();
  return usePouchCreate(settings, ["settings"]);
}

export function useCreateFormTemplate() {
  const { formTemplates } = useDatabases();
  return usePouchCreate(formTemplates, ["formTemplates"]);
}
export function useCreateObject() {
  const { inventory } = useInventoryDatabases();
  const { inventoryId } = useCurrentInventoryId();
  return usePouchCreate<InventoryGeometryItem>(
    inventory as PouchDB.Database<InventoryGeometryItem>,
    inventoryId === undefined ? [] : [`inventory:${inventoryId}`]
  );
}
